import { SharedChatInput } from "@/shared/components/chat";
import ZaniaInverseIcon from "@/shared/icons/zania-inverse";
import { useSocketData } from "@/shared/states/sockets";
import { UploadedFile } from "@/shared/types/file-upload";
import { Button, Spinner } from "@nextui-org/react";
import { useClipboard } from "@nextui-org/use-clipboard";
import { Check, Copy, PaperclipIcon, User } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import ReactMarkdown from 'react-markdown';
import { ChatMessage } from "../../types";
import { useSendTPEMessage } from "../chat/requests";
import { useTPEChatStore } from "../chat/states";

// Component to handle message content rendering
const FormattedMessage: React.FC<{ content: string }> = ({ content }) => {
  // Check if content contains HTML (safe HTML from backend)
  if (content.includes('<div class="bg-gray-100') && content.includes('<ol>')) {
    return <div dangerouslySetInnerHTML={{ __html: content }} />;
  }

  // Preprocess content to extract and preserve list numbers across sections
  let listCounter = 0;
  const processedContent = content
    .split('\n')
    .map(line => {
      const trimmed = line.trimStart();
      // Check if line starts with a number (e.g., "5. Text")
      const match = trimmed.match(/^(\d+)\.\s/);
      if (match) {
        listCounter = parseInt(match[1]);
      }
      return trimmed;
    })
    .join('\n');

  // Always use ReactMarkdown for proper markdown rendering (handles bold, italics, lists, etc.)
  let currentNumber = 0;
  return (
    <ReactMarkdown
      components={{
        // Customize paragraph rendering to handle line breaks
        p: ({ children }) => <div className="mb-2 last:mb-0 ml-2">{children}</div>,
        // Make bold text stand out
        strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
        // Make italic text stand out
        em: ({ children }) => <em className="italic">{children}</em>,
        // Style headings
        h1: ({ children }) => <h1 className="text-xl font-bold mb-3 mt-4">{children}</h1>,
        h2: ({ children }) => <h2 className="text-lg font-bold mb-3 mt-4">{children}</h2>,
        h3: ({ children }) => <h3 className="text-base font-bold mb-2 mt-3">{children}</h3>,
        h4: ({ children }) => <h4 className="text-sm font-bold mb-2 mt-2">{children}</h4>,
        // Style horizontal rules
        hr: () => <hr className="my-3 border-t border-gray-300" />,
        // Style lists appropriately with better spacing
        ol: ({ children, start }: any) => (
          <ol
            className="list-decimal ml-6 space-y-1 my-2"
            start={start}
          >
            {children}
          </ol>
        ),
        ul: ({ children }) => <ul className="list-disc ml-6 space-y-1 my-2">{children}</ul>,
        // Style list items - detect and preserve original numbers from markdown
        li: ({ children, ...props }: any) => {
          // Try to extract number from the original markdown (before ReactMarkdown parses it)
          // Check if this is part of content that had a number
          const textContent = React.Children.toArray(children).join('');
          const numberMatch = processedContent.match(new RegExp(`(\\d+)\\.\\s+${textContent.substring(0, 30).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`));

          if (numberMatch) {
            const num = parseInt(numberMatch[1]);
            return (
              <li className="ml-1 pl-1" value={num} {...props}>
                {children}
              </li>
            );
          }

          return (
            <li className="ml-1 pl-1" {...props}>
              {children}
            </li>
          );
        },
        // Prevent code blocks from rendering - treat as normal text instead
        code: ({ node, inline, className, children, ...props }: any) => {
          // If it's inline code (backticks), render with styling
          if (inline) {
            return <code className="bg-gray-200 px-1 py-0.5 rounded text-sm">{children}</code>;
          }
          // If it's a code block, just render as normal paragraph (don't treat as code)
          return <div className="whitespace-pre-wrap">{children}</div>;
        },
        pre: ({ children }) => <div>{children}</div>, // Remove pre formatting
      }}
    >
      {processedContent}
    </ReactMarkdown>
  );
};

// Component to render assistant message with copy functionality
const AssistantMessage: React.FC<{ content: string }> = ({ content }) => {
  const { copy, copied } = useClipboard();

  const handleCopy = () => {
    copy(content);
  };

  return (
    <div className="relative group">
      <div className="bg-gray-100 rounded-lg p-3 pr-12 pb-10 text-sm text-gray-800">
        <FormattedMessage content={content} />
      </div>
      <div className="absolute right-2 bottom-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <Button 
          isIconOnly 
          radius="full" 
          size="sm" 
          variant="light" 
          onPress={handleCopy}
          className="bg-white shadow-sm"
        >
          {copied ? (
            <Check className="text-green-500" size={16} />
          ) : (
            <Copy className="text-gray-600" size={16} />
          )}
        </Button>
      </div>
    </div>
  );
};

export interface SimpleTPEChatProps {
  onFileUpload?: (files: File[]) => void;
  uploadedFiles?: UploadedFile[];
  onRemoveFile?: (fileId: string) => void;
  className?: string;
  token?: string;
  chatMessages?: ChatMessage[];
  isLoadingMessages?: boolean;
  sessionId?: string;
  onMessageSent?: (message: ChatMessage) => void;
}

const SimpleTPEChat: React.FC<SimpleTPEChatProps> = ({
  onFileUpload,
  uploadedFiles = [],
  onRemoveFile,
  className,
  token,
  chatMessages = [],
  isLoadingMessages = false,
  sessionId,
  onMessageSent,
}) => {
  const [message, setMessage] = useState("");
  const sendMessageMutation = useSendTPEMessage();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  
  // Use TPE Chat store for isGenerating state
  const { isGenerating, setIsGenerating, setContext } = useTPEChatStore();
  
  // Get the existing socket from the global store
  const socketData = useSocketData('vendorForm');

  // Setup socket event listeners for AI message streaming
  useEffect(() => {
    if (!sessionId) return;

    // Set context in the store
    setContext({ chatId: sessionId, token });

    // Use the existing socket from the global store
    const socket = socketData?.socket;
    if (!socket) return;

    const handleStreamingAIMessage = (data: any) => {
      // Check if this message is for the current chat session
      if (!data.chat_id || data.chat_id !== sessionId) {
        return;
      }

      // Remove "Thinking..." as soon as we get any AI message event
      setIsGenerating(false);
    };

    socket.on('tpra:followup:chat:ai:message', handleStreamingAIMessage);

    return () => {
      socket.off('tpra:followup:chat:ai:message', handleStreamingAIMessage);
    };
  }, [sessionId, socketData?.socket, setIsGenerating, setContext, token]);

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Check if user is near bottom of chat
  const isNearBottom = () => {
    if (!messagesContainerRef.current) return true;
    const container = messagesContainerRef.current;
    const threshold = 100; // pixels from bottom
    return container.scrollHeight - container.scrollTop - container.clientHeight < threshold;
  };

  useEffect(() => {
    if (isNearBottom()) {
      scrollToBottom();
    }
  }, [chatMessages]);

  // Always scroll to bottom on initial load when messages are loaded
  useEffect(() => {
    if (chatMessages.length > 0 && !isLoadingMessages) {
      setTimeout(() => scrollToBottom(), 100);
    }
  }, [chatMessages.length, isLoadingMessages]);

  const handleMessageChange = (newMessage: string) => {
    setMessage(newMessage);
  };

  const handleSendMessage = async () => {
    // Allow sending if there's either a message or files, but not both empty
    if ((!message.trim() && uploadedFiles.length === 0) || !token || !sessionId) return;

    const messageText = message.trim();

    // Convert uploaded files to attachment format (only include successfully uploaded files)
    const attachments = uploadedFiles
      .filter(file => file.status === 'success' && file.signedUrl)
      .map(file => ({
        id: file.id,
        file_name: file.file.name,
        signed_url: file.signedUrl!
      }));

    // Immediately add user message to chat
    const userMessage: ChatMessage = {
      id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      chat_id: sessionId,
      role: "user",
      type: "text",
      created_at: new Date().toISOString(),
      text: messageText,
      attachments: attachments,
    };

    // Notify parent component to add the message immediately
    if (onMessageSent) {
      onMessageSent(userMessage);
    }

    // Scroll to bottom immediately after sending user message
    setTimeout(() => scrollToBottom(), 100);

    // Clear input and restore focus
    setMessage("");
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);

    // Clear uploaded files after sending
    if (onRemoveFile) {
      uploadedFiles.forEach(file => {
        onRemoveFile(file.id);
      });
    }

    try {
      // Show "Thinking..." when sending message
      setIsGenerating(true);
      
      const response = await sendMessageMutation.mutateAsync({
        session_id: sessionId,
        message: messageText,
        token: token,
        attachments: attachments, // Include attachments in the API call
      });
    } catch (error) {
      console.error("Failed to send message:", error);
      // Remove "Thinking..." on error
      setIsGenerating(false);
    }
  };

  return (
    <div className={`w-full h-full flex flex-col min-h-0 ${className}`}>
      {/* Header */}
      <div className="bg-white border border-[#E4E4E7] rounded-t-xl flex-shrink-0">
        <div
          className="flex flex-row justify-between items-center bg-white w-full h-[60px] rounded-t-xl"
          style={{ padding: "12px 16px", boxSizing: "border-box" }}
        >
          {/* Left Section - Assessment Title */}
          <div className="flex flex-row items-center flex-1" style={{ padding: "0px", gap: "16px", height: "36px" }}>
            <h2 className="text-sm font-medium text-[#171717]">Complete Your Submission</h2>
          </div>
        </div>
      </div>

      {/* Chat Messages Area - Scrollable with flex-1 to take remaining space */}
      <div
        ref={messagesContainerRef}
        className="flex-1 bg-white border-l border-r border-[#E4E4E7] overflow-y-auto min-h-0 max-h-full"
      >
        <div className="p-4 pb-0">
          {isLoadingMessages ? (
            <div className="flex items-center justify-center h-32">
              <Spinner size="sm" />
              <span className="ml-2 text-sm text-gray-500">Loading messages...</span>
            </div>
          ) : (
            <div className="space-y-4">
              {chatMessages.map((message) => (
                <div key={message.id} className="flex items-start gap-3">
                  {message.role === "assistant" ? (
                    // AI Message
                    <>
                      <ZaniaInverseIcon fill="white" />
                      <div className="flex-1">
                        <AssistantMessage content={message.text} />
                      </div>
                    </>
                  ) : (
                    // User Message
                    <>
                      <div className="w-8 h-8 bg-gradient-to-b from-pink-300 to-purple-400 rounded-full flex items-center justify-center flex-shrink-0">
                          <User className="text-white" size={18} fill="white" />
                      </div>
                      <div className="flex-1">
                        <div className="bg-gray-200 rounded-lg p-3 text-sm text-gray-800">
                            {message.text && <FormattedMessage content={message.text} />}
                            {/* Show attachments if available - New design */}
                          {message.role === "user" && message.attachments && message.attachments.length > 0 && (
                              <div className={`${message.text ? 'p-1 ml-1' : ''} flex flex-col items-start gap-3 rounded-xl`}>
                                <div className="flex flex-row items-start">
                                  <PaperclipIcon className="w-4 h-4 text-[#3F3F46] flex-shrink-0 mr-1.5 mt-0.5" />
                                  <div className="flex flex-wrap items-center">
                                    {message.attachments.map((attachment, idx) => (
                                      <React.Fragment key={attachment.id}>
                                        <a
                                          href={attachment.signed_url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className={`${message.text ? `pb-1` : ''} text-sm text-[#3F3F46] underline hover:text-[#27272A]`}
                                        >
                                          {attachment.file_name}
                                        </a>
                                        {idx < message.attachments.length - 1 && <span className="text-sm text-[#3F3F46]">,&nbsp;</span>}
                                      </React.Fragment>
                                    ))}
                                  </div>
                                </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ))}
              {/* Thinking indicator */}
              {isGenerating && (
                <div className="flex items-start gap-3">
                  <ZaniaInverseIcon fill="white" />
                  <div className="flex text-sm text-purple-500">
                    Thinking<span className="pl-1 animate-bounce">..</span>
                  </div>
                </div>
              )}
              {/* Invisible element to scroll to */}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>
      </div>

      {/* Chat Input - Sticky at bottom */}
      <div className="bg-white flex-shrink-0">
        <div className="p-4">
          <SharedChatInput
            ref={inputRef}
            message={message}
            onMessageChange={handleMessageChange}
            onSendMessage={handleSendMessage}
            isGenerating={isGenerating}
            isDisabled={isGenerating}
            placeholder="Enter your response here"
            maxLength={10000}
            showFileUpload={true}
            showCharacterCount={true}
            onFileUpload={onFileUpload}
            uploadedFiles={uploadedFiles.map(file => ({ name: file.file.name, id: file.id }))}
            onRemoveFile={onRemoveFile}
            fileUploadError={false}
            maxFiles={10}
            allowedFileTypes={[
              "application/json",
              "application/pdf",
              "application/msword",
              "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            ]}
            fileTypesTooltipText="doc, json, pdf"
          />
        </div>
      </div>
    </div>
  );
};

export default SimpleTPEChat;
